import { transformVendorTier } from '@utils/transformVendorTier';
import type { ImpressionType } from 'types/vendorImpressions';
import * as actionCreators from './actionCreators';
import report from './reportError';
import {
	determineVendorTier,
	getAppliedFiltersForBucketedImpressions,
	getBucketedImpressions,
	getListingDetails,
	getSortScoreId,
	isOnDirectoryPage,
} from './utils';

const DEFAULT_TRANSMIT_LIMIT = 12;
/** @internal */
export const IMPRESSION_QUEUE_IDLE_TIMEOUT = 60 * 1000; // 1 minute

// Data team would prefer us to send in batches of 12,
// but occasionally we need to flush the queue when there are fewer
// such as (when idle)
type TransmitMinimumLimit = 1 | typeof DEFAULT_TRANSMIT_LIMIT;

type AnalyticsWithTrack = {
	track?: (eventName: string, properties: Record<string, unknown>) => void;
};

const isActivityBasedRightRailImpression = (
	impressionType?: string,
): boolean => {
	if (!impressionType) {
		return false;
	}

	const normalizedType = impressionType.toLowerCase();
	return (
		normalizedType.includes('directory - right rail') &&
		(normalizedType.includes('lead') || normalizedType.includes('favorite'))
	);
};

const getActivityBasedRightRailFormula = (): string => 'similarity ros';

const getDirectoryFormula = (
	sortType: string,
	impressionType?: string,
): string => {
	if (impressionType?.includes('Directory - Right Rail')) {
		if (
			impressionType?.includes('lead') ||
			impressionType?.includes('favorite')
		) {
			return 'vrm';
		}

		return 'pvRec anon';
	}

	const sortFormulas: Record<string, string> = {
		recommended: 'sort by recommended',
		featured: 'sort by featured',
		'review-rating': 'sort by review-rating',
	};
	return sortFormulas[sortType] || `sort by ${sortType}`;
};

const getStorefrontFormula = (impressionType: string): string => {
	const isCrossCategoryWidget =
		impressionType.includes('Center') || impressionType.includes('middle');

	return isCrossCategoryWidget ? 'pvrec cross category ros' : 'similarity ros';
};

const getImpressionTypeFormula = (impressionType: string): string => {
	if (impressionType.includes('Storefront')) {
		return getStorefrontFormula(impressionType);
	}

	if (impressionType.includes('Directory - Right Rail')) {
		if (
			impressionType?.includes('lead') ||
			impressionType?.includes('favorite')
		) {
			return 'vrm';
		}

		return 'pvRec anon';
	}

	return 'banded distance';
};

const getPaiverRightRailFormula = (isMember: boolean): string => {
	return isMember ? 'paiver ros' : 'paiver ros anon';
};

export const getSourceFormula = (
	reduxState: Redux.State,
	isSemanticSearchCalled?: boolean,
	impressionType?: string,
): string => {
	if (isSemanticSearchCalled) {
		return 'semantic';
	}

	if (isOnDirectoryPage(reduxState) && reduxState.search.sort) {
		if (impressionType?.includes('Directory - Right Rail')) {
			if (isActivityBasedRightRailImpression(impressionType)) {
				return getActivityBasedRightRailFormula();
			}

			const isMember = Boolean(reduxState.membership.member?.id);
			return getPaiverRightRailFormula(isMember);
		}
		return getDirectoryFormula(reduxState.search.sort, impressionType);
	}

	return impressionType
		? getImpressionTypeFormula(impressionType)
		: 'banded distance';
};

export interface TransmitImpressionsOptions {
	transmitMinimumLimit?: TransmitMinimumLimit;
	location?: Redux.Location;
	isSemanticSearchCalled?: boolean;
	appliedFilters?: Record<string, string[]> | null;
	categoryCodes?: string[];
	categoryCodesScopeToImpressionType?: ImpressionType;
}

export const transmitImpressions =
	(options: TransmitImpressionsOptions = {}): Redux.ThunkResult<void> =>
	async (dispatch, getState) => {
		const {
			transmitMinimumLimit = DEFAULT_TRANSMIT_LIMIT,
			location,
			isSemanticSearchCalled,
			appliedFilters,
			categoryCodes,
			categoryCodesScopeToImpressionType,
		} = options;

		const reduxState = getState();
		const { impressionQueue: queue } = reduxState.vendorImpressions;
		const sentImpressions: VendorImpressions.Impression[] = [];
		while (queue.length >= transmitMinimumLimit) {
			const impressions = queue.splice(0, DEFAULT_TRANSMIT_LIMIT);

			if (window?.analytics) {
				const { code: categoryCode } = reduxState.category;
				let city: string, stateCode: string;

				if (location) {
					city = location.city ?? '';
					stateCode = location.stateCode ?? '';
				} else {
					city = reduxState.location.city ?? '';
					stateCode = reduxState.location.stateCode ?? '';
				}

				const marketCode = reduxState.search.marketCode ?? '';
				const impressionBuckets = getBucketedImpressions(
					impressions,
					reduxState,
				);
				const analytics = window.analytics as AnalyticsWithTrack | undefined;

				const appliedFiltersForImpressions =
					getAppliedFiltersForBucketedImpressions(reduxState, appliedFilters);
				(Object.keys(impressionBuckets) as ImpressionType[]).forEach(
					(impressionType) => {
						const shouldUseCategoryCodesOverride =
							categoryCodes !== undefined &&
							(categoryCodesScopeToImpressionType === undefined ||
								categoryCodesScopeToImpressionType === impressionType);

						const trackingCategoryCode = shouldUseCategoryCodesOverride
							? categoryCodes.length > 0
								? categoryCodes.join(',')
								: null
							: categoryCode;

						analytics?.track?.('Vendor Tile Viewed', {
							product_listing_type: impressionType,
							category_id: trackingCategoryCode,
							region_id: marketCode,
							display_location_name: `${city}, ${stateCode}`,
							sort_type: isSemanticSearchCalled
								? 'semantic'
								: isOnDirectoryPage(reduxState)
									? reduxState.search.sort
									: null,
							directory_filters: appliedFiltersForImpressions,
							page_number: reduxState.search.pagination.page,
							vendor_impression_list: impressionBuckets[impressionType],
							source_formula: getSourceFormula(
								reduxState,
								isSemanticSearchCalled,
								impressionType,
							),
						});
					},
				);
			}

			sentImpressions.push(...impressions);
		}

		dispatch(actionCreators.clearTransmittedVendors(sentImpressions));

		// If there are still items in the queue, restart the idle timer
		if (queue.length > 0) {
			dispatch(
				setIdleTimer(
					location || reduxState.location,
					isSemanticSearchCalled,
					appliedFilters,
					categoryCodes,
					categoryCodesScopeToImpressionType,
				),
			);
		}

		// If queue is empty, clear the current timeout
		if (queue.length === 0) {
			dispatch(actionCreators.setCurrentTimeout(undefined));
		}
	};

/** @internal */
export const setIdleTimer = (
	location: Redux.Location,
	isSemanticSearchCalled?: boolean,
	appliedFilters?: Record<string, string[]> | null,
	categoryCodes?: string[],
	categoryCodesScopeToImpressionType?: ImpressionType,
): Redux.ThunkResult<void> => {
	return async (dispatch, getState) => {
		const { currentTimeout } = getState().vendorImpressions;
		clearTimeout(currentTimeout);
		const timeout = window.setTimeout(() => {
			dispatch(
				transmitImpressions({
					transmitMinimumLimit: 1,
					location,
					isSemanticSearchCalled,
					appliedFilters,
					categoryCodes,
					categoryCodesScopeToImpressionType,
				}),
			);
		}, IMPRESSION_QUEUE_IDLE_TIMEOUT);

		dispatch(actionCreators.setCurrentTimeout(timeout));
	};
};

/**
 * Cancels any pending idle timer and immediately flushes the entire impression
 * queue. Use this when the tracking context changes (e.g. MCVRM category_id)
 * so that stale-context timers cannot transmit impressions with outdated metadata.
 */
export const flushImpressionQueue =
	(
		options: Omit<TransmitImpressionsOptions, 'transmitMinimumLimit'> = {},
	): Redux.ThunkResult<void> =>
	(dispatch, getState) => {
		const { currentTimeout } = getState().vendorImpressions;
		clearTimeout(currentTimeout);
		dispatch(actionCreators.setCurrentTimeout(undefined));
		dispatch(transmitImpressions({ ...options, transmitMinimumLimit: 1 }));
	};

export const queueVendorImpression =
	(
		details: VendorImpressions.ImpressionDetails,
		isSemanticSearchCalled?: boolean,
		appliedFilters?: Record<string, string[]> | null,
		categoryCodes?: string[],
	): Redux.ThunkResult<Promise<void>> =>
	async (dispatch, getState) => {
		const { locationIndex, impressionType, vendor, cardUiLocation } = details;
		const categoryCodesScopeToImpressionType = impressionType;
		const reduxState = getState();
		const { currentTimeout, directoryStyle, impressionQueue } =
			reduxState.vendorImpressions;

		// Use appliedFilters as-is - all components now explicitly pass this parameter
		const filtersToUse = appliedFilters ?? null;

		const position =
			locationIndex > 0
				? locationIndex
				: report.position(locationIndex, details);

		const { impression_style: impressionStyle } = JSON.parse(
			getListingDetails(reduxState, cardUiLocation),
		); // TODO: SafeGuard

		if (!directoryStyle) {
			dispatch(actionCreators.setDirectoryStyle(impressionStyle));
		}

		// Clear the queue if we switch from image to map (or vice versa) on directory pages
		if (directoryStyle && directoryStyle !== impressionStyle) {
			clearTimeout(currentTimeout);
			dispatch(
				transmitImpressions({
					transmitMinimumLimit: 1,
					location: reduxState.location,
					isSemanticSearchCalled,
					appliedFilters: filtersToUse,
					categoryCodes,
					categoryCodesScopeToImpressionType,
				}),
			);
			dispatch(actionCreators.setDirectoryStyle(impressionStyle));
		}

		const vendorTier = determineVendorTier(
			vendor,
			reduxState.search.marketCode,
		);

		const isLiteStorefront = vendor?.purchaseStatus !== 'PAID';
		const aiPhoto =
			!isLiteStorefront && vendor.settings?.AIPhoto
				? vendor.ai?.recommendedPhoto
				: null;
		const aiPhotoId = vendor.settings?.AIPhoto ? aiPhoto?.id || null : null;

		const impression: VendorImpressions.Impression = {
			ai_photo_id: aiPhotoId,
			directory_sort_score_id: getSortScoreId(reduxState, vendor),
			id: vendor.id,
			list: impressionType,
			name: vendor.name,
			position,
			storefront_card_id: vendor.storefrontCard?.id || null,
			storefront_card_media_id:
				vendor.storefrontCard?.url.split('/').pop() || null,
			variant: transformVendorTier(vendorTier),
			storefront_completion_status: vendor.statistics?.completionScore || null,
			impression_timestamp: new Date().toISOString(),
		};
		dispatch(actionCreators.queueVendorImpression(impression));
		// We need to send state page impressions one at a time.
		const isStatePage = reduxState.page.pageType === 'state';
		const canTransmit =
			[...impressionQueue, impression].length >= DEFAULT_TRANSMIT_LIMIT ||
			isStatePage;

		if (canTransmit) {
			clearTimeout(currentTimeout);
			const transmitMinimumLimit = isStatePage ? 1 : DEFAULT_TRANSMIT_LIMIT;
			dispatch(
				transmitImpressions({
					transmitMinimumLimit,
					location: reduxState.location,
					isSemanticSearchCalled,
					appliedFilters: filtersToUse,
					categoryCodes,
					categoryCodesScopeToImpressionType,
				}),
			);
		} else {
			dispatch(
				setIdleTimer(
					reduxState.location,
					isSemanticSearchCalled,
					filtersToUse,
					categoryCodes,
					categoryCodesScopeToImpressionType,
				),
			);
		}
	};
